<template>
  <div class="osl-page">
    <div class="osl-container">
      <header class="osl-head">
        <p class="osl-kicker">Legal</p>
        <h1 class="osl-title">Open Source Licenses</h1>
        <p class="osl-updated" v-if="data">Last updated {{ formattedGeneratedAt }}</p>
      </header>

      <p class="osl-lead">
        The Kiey Service is built with open-source software. We're grateful to the maintainers of
        the projects below. The list reflects direct production dependencies declared in the Kiey
        web app and Kiey Cloud Functions backend, and is updated periodically.
      </p>

      <p class="osl-public-link">
        Looking for the public-facing version? Visit
        <a href="https://kiey.com/licenses" target="_blank" rel="noopener noreferrer">kiey.com/licenses</a>.
      </p>

      <div v-if="loading" class="osl-loading">Loading licenses…</div>

      <div v-else-if="error" class="osl-error">
        <p><strong>Couldn't load licenses.</strong></p>
        <p class="osl-error-detail">{{ error }}</p>
      </div>

      <template v-else-if="data">
        <div class="osl-toolbar">
          <input
            v-model="search"
            type="search"
            class="osl-search"
            placeholder="Search packages or licenses…"
            aria-label="Search packages"
          />
          <p class="osl-summary">
            <strong>{{ filteredPackages.length }}</strong>
            of <strong>{{ data.total }}</strong> packages
            <span v-if="breakdown.length"> &middot; {{ breakdown }}</span>
          </p>
        </div>

        <div class="osl-table-wrap">
          <table class="osl-table">
            <thead>
              <tr>
                <th @click="setSort('name')" :class="sortClass('name')">Package</th>
                <th @click="setSort('version')" :class="sortClass('version')">Version</th>
                <th @click="setSort('license')" :class="sortClass('license')">License</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in filteredPackages" :key="pkg.name">
                <td>
                  <span class="osl-pkg-name">{{ pkg.name }}</span>
                  <span v-if="pkg.description" class="osl-pkg-desc">{{ pkg.description }}</span>
                </td>
                <td class="osl-pkg-version">{{ pkg.version }}</td>
                <td>
                  <span class="osl-license-badge">{{ pkg.license || 'UNKNOWN' }}</span>
                </td>
                <td>
                  <a
                    v-if="repoUrl(pkg)"
                    :href="repoUrl(pkg)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >View source</a>
                  <span v-else class="osl-muted">—</span>
                </td>
              </tr>
              <tr v-if="!filteredPackages.length">
                <td colspan="4" class="osl-empty">No packages match "{{ search }}".</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="osl-footnote">
          Transitive (sub-) dependencies inherit the licenses of their upstream sources and are
          governed by their own respective notices. Trademarks, logos, and brand names referenced
          in any license remain the property of their respective owners. Kiey's own application
          code, designs, and content are not open-sourced and remain proprietary to Kiey Holdings
          Ltd. — see the
          <a href="https://kiey.com/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          for details.
        </p>

        <p class="osl-contact">
          License inquiries:
          <a href="mailto:support@kiey.com?subject=Open%20Source%20License%20Inquiry">support@kiey.com</a>
        </p>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "OpenSourceLicenses",
  data() {
    return {
      loading: true,
      error: null,
      data: null,
      search: "",
      sortKey: "name",
      sortDir: "asc",
    };
  },
  computed: {
    formattedGeneratedAt() {
      if (!this.data || !this.data.generated_at) return "";
      try {
        const d = new Date(this.data.generated_at);
        if (isNaN(d.getTime())) return this.data.generated_at;
        return d.toISOString().slice(0, 10);
      } catch (e) {
        return this.data.generated_at;
      }
    },
    breakdown() {
      if (!this.data || !this.data.by_license) return "";
      const entries = Object.entries(this.data.by_license).sort((a, b) => b[1] - a[1]);
      return entries.map(([lic, n]) => `${lic} ${n}`).join(" · ");
    },
    filteredPackages() {
      if (!this.data || !this.data.packages) return [];
      const q = (this.search || "").trim().toLowerCase();
      let list = this.data.packages.slice();
      if (q) {
        list = list.filter((p) => {
          return (
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.license && p.license.toLowerCase().includes(q)) ||
            (p.description && p.description.toLowerCase().includes(q))
          );
        });
      }
      const dir = this.sortDir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        const av = (a[this.sortKey] || "").toString().toLowerCase();
        const bv = (b[this.sortKey] || "").toString().toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
      return list;
    },
  },
  mounted() {
    this.loadLicenses();
  },
  methods: {
    async loadLicenses() {
      this.loading = true;
      this.error = null;
      try {
        const url = (this.publicPath || "/") + "legal/licenses.json";
        const res = await fetch(url, { cache: "no-cache" });
        if (!res.ok) throw new Error("HTTP " + res.status);
        this.data = await res.json();
      } catch (e) {
        this.error = e && e.message ? e.message : "Unknown error.";
      } finally {
        this.loading = false;
      }
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    sortClass(key) {
      if (this.sortKey !== key) return "osl-th-sort";
      return "osl-th-sort osl-th-sort-active osl-th-sort-" + this.sortDir;
    },
    repoUrl(pkg) {
      if (!pkg) return "";
      let r = pkg.repository || "";
      if (!r && pkg.homepage) return pkg.homepage;
      if (!r) return "";
      r = r.replace(/^git\+/, "").replace(/^git:\/\//, "https://").replace(/\.git$/, "");
      if (r.startsWith("github:")) r = "https://github.com/" + r.slice("github:".length);
      if (r.startsWith("git@github.com:")) {
        r = "https://github.com/" + r.slice("git@github.com:".length).replace(/\.git$/, "");
      }
      if (!/^https?:\/\//.test(r)) return pkg.homepage || "";
      return r;
    },
  },
};
</script>

<style scoped>
.osl-page {
  background: #f7f8fb;
  min-height: calc(100vh - 120px);
  padding: 32px 16px 80px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #061D47;
}
.osl-container {
  max-width: 980px;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  padding: 36px clamp(20px, 4vw, 44px) 44px;
  box-shadow: 0 1px 2px rgba(6, 29, 71, 0.04), 0 8px 24px rgba(6, 29, 71, 0.06);
}
.osl-head {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(6, 29, 71, 0.08);
}
.osl-kicker {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #5A6A85;
  font-size: 11px;
  font-weight: 700;
  margin: 0 0 6px;
}
.osl-title {
  font-size: clamp(1.6rem, 3.4vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
  color: #061D47;
}
.osl-updated {
  color: #5A6A85;
  font-size: 0.9rem;
  margin: 0;
}
.osl-lead {
  color: #2c3a55;
  line-height: 1.6;
  font-size: 0.98rem;
  margin: 18px 0 8px;
}
.osl-public-link {
  color: #5A6A85;
  font-size: 0.9rem;
  margin: 0 0 24px;
}
.osl-public-link a { color: #061D47; font-weight: 600; }
.osl-loading,
.osl-error {
  text-align: center;
  padding: 32px;
  color: #5A6A85;
  font-size: 0.95rem;
}
.osl-error { color: #b3261e; }
.osl-error-detail { font-size: 0.85rem; color: #5A6A85; margin-top: 6px; }

.osl-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 16px 0 12px;
  flex-wrap: wrap;
}
.osl-search {
  flex: 1 1 260px;
  min-width: 200px;
  padding: 10px 14px;
  border: 1px solid rgba(6, 29, 71, 0.18);
  border-radius: 999px;
  font-size: 0.95rem;
  color: #061D47;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.osl-search:focus {
  border-color: #061D47;
  box-shadow: 0 0 0 3px rgba(6, 29, 71, 0.12);
}
.osl-summary {
  margin: 0;
  color: #5A6A85;
  font-size: 0.88rem;
}

.osl-table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(6, 29, 71, 0.08);
  border-radius: 12px;
}
.osl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}
.osl-table th,
.osl-table td {
  padding: 12px 14px;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid rgba(6, 29, 71, 0.06);
}
.osl-table th {
  background: rgba(6, 29, 71, 0.03);
  color: #061D47;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.osl-th-sort { cursor: pointer; user-select: none; }
.osl-th-sort::after {
  content: ' ↕';
  opacity: 0.3;
  font-size: 0.85em;
}
.osl-th-sort-active::after {
  opacity: 1;
}
.osl-th-sort-asc::after { content: ' ↑'; }
.osl-th-sort-desc::after { content: ' ↓'; }

.osl-table tbody tr:last-child td { border-bottom: none; }
.osl-table tbody tr:hover { background: rgba(6, 29, 71, 0.025); }
.osl-pkg-name {
  display: block;
  font-weight: 600;
  color: #061D47;
  word-break: break-word;
}
.osl-pkg-desc {
  display: block;
  font-size: 0.82rem;
  color: #5A6A85;
  margin-top: 2px;
  line-height: 1.4;
}
.osl-pkg-version {
  font-variant-numeric: tabular-nums;
  color: #5A6A85;
  white-space: nowrap;
}
.osl-license-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(6, 29, 71, 0.08);
  color: #061D47;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
}
.osl-table td a {
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 500;
}
.osl-table td a:hover { text-decoration: underline; }
.osl-empty {
  text-align: center;
  color: #5A6A85;
  padding: 28px 14px;
}
.osl-muted { color: #9aa4b8; }

.osl-footnote {
  font-size: 0.85rem;
  color: #5A6A85;
  line-height: 1.6;
  margin-top: 22px;
}
.osl-footnote a { color: #061D47; font-weight: 600; }
.osl-contact {
  font-size: 0.92rem;
  color: #2c3a55;
  margin-top: 14px;
}
.osl-contact a { color: #1d4ed8; font-weight: 600; }

@media (max-width: 600px) {
  .osl-container { padding: 24px 16px 32px; border-radius: 12px; }
  .osl-pkg-desc { display: none; }
  .osl-table th, .osl-table td { padding: 10px 8px; }
}
</style>
